import React, { useContext, useEffect, useState } from "react";
import { PageHeading } from "../ui-components/Container";
import { useIntl } from "react-intl";
import Select from "../ui-components/Select";
import CountryConfigContext from "../CountryConfigContext";
import AuthenticationService from "../services/AuthenticationService";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../ui-components/Notifications";
import KCCTintometersSurveysTable from "../components/Tables/KCCTintometersSurveysTable";
import CountryContext from "../CountryContext";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import Button from "../ui-components/Button";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { useHistory } from "react-router-dom";
import Toggle from "../ui-components/Toggle";
import KCCWorksitesSurveysTable from "../components/Tables/KCCWorksitesSurveysTable";
import {
  buildQueryParams,
  buildFilterFromQuery,
  orderingFromQuery,
} from "@utils/pagination";

const Tintometers = () => {
  const intl = useIntl();
  const history = useHistory();
  const countryApiRequest = useCountryAxiosInstance();

  // context
  const { country } = useContext(CountryContext);
  const {
    config: { services },
  } = useContext(CountryConfigContext);
  const { push } = useContext(NotificationsContext);

  // utilities
  const userData = AuthenticationService.getUserData();
  const tintometerServices =
    services?.filter((s) => s.type === "tintometer") ?? [];
  const serviceOptions =
    tintometerServices?.length > 0
      ? tintometerServices.map((s) => ({ name: s.name, value: s.id }))
      : [{ name: "-", value: -1 }];

  // state
  const [colors, setColors] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(
    serviceOptions[0].value
  );
  const [surveys, setSurveys] = useState(null);
  const [isLoadingWorksitesSurveys, setIsLoadingWorksitesSurveys] =
    useState(false);
  const [tintometerSurveys, setTintometerSurveys] = useState(null);
  const [submittingNewSurvey, setSubmittingNewSurvey] = useState(false);

  const selectedService =
    selectedServiceId > 0
      ? tintometerServices.find((s) => s.id === selectedServiceId)
      : null;

  const fetchSurveys = async () => {
    setTintometerSurveys(null);
    try {
      setIsLoadingWorksitesSurveys(true);
      const [resultT] = await Promise.all([
        countryApiRequest.get("/service_surveys/from_user", {
          params: {
            include_closed: includeClosed,
            service_id: selectedService.id,
            for_worksite: false,
          },
        }),
      ]);
      setTintometerSurveys(resultT["result"]);
    } catch (err) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      throw err;
    } finally {
      setIsLoadingWorksitesSurveys(false);
    }
  };

  const createNewSurvey = async () => {
    setSubmittingNewSurvey(true);
    try {
      const permissions = ["admin", `user_${userData.id}`];
      const newSurvey = await countryApiRequest.post("/service_surveys", {
        name: "",
        service_id: selectedService.id,
        worksite_id: null,
        id_source_surveys: [],
        complete: false,
        closed: false,
        blocked: false,
        r_perm: permissions,
        w_perm: permissions,
        id_creator: userData.id,
        service_version: selectedService.active_version,
        flg_specified: false,
      });
      await fetchSurveys()
      // go to survey page
      history.push(
        `/${country.code}/service/${selectedService.id}/survey/${newSurvey.id}`
      );
    } catch (err) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "server_error" }),
      });
      throw err;
    } finally {
      setSubmittingNewSurvey(false);
    }
  };

  const deleteSurvey = async (sid, onSafeFinish) => {
    try {
      await countryApiRequest.delete(`/service_surveys/${sid}`);
      await fetchSurveys()
      push({
        type: "success",
        title: intl.formatMessage({ id: "survey_delete_success" }),
      });
    } catch (err) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "server_error" }),
      });
      throw err;
    } finally {
      onSafeFinish();
    }
  };

  const generateTintometristSurvey = async (selectedSurvey, onSafeFinish) => {
    if (!selectedServiceId)
      throw Error("no service selected");
    try {
      await countryApiRequest.post(
        `/service_surveys/generate_tintometrist_duplicate/${selectedSurvey.id}`,
        {
          tintometer_service_id: selectedServiceId,
          generated_survey_name: intl.formatMessage(
            { id: "autogenerated_tintometrist_survey_name" },
            {
              serviceName: selectedSurvey?.service_name?.toUpperCase(),
              worksiteName: selectedSurvey?.worksite_name?.toUpperCase(),
            }
          ),
        }
      );
      // update surveys to include the just generated one
      await fetchSurveys();
      push({
        type: "success",
        title: intl.formatMessage({ id: "survey_generated_success" }),
      });
    } catch (err) {
      const mess = err?.response?.data?.detail;
      push({
        type: "error",
        title: intl.formatMessage({ id: mess, defaultMessage: mess }),
      });
      throw err;
    } finally {
      onSafeFinish();
    }
  };

  useEffect(() => {
    countryApiRequest
      .get("/tintometers/colors/all")
      .then((res) => {
        setColors(res);
      })
      .catch((err) => {
        push({
          type: "error",
          title: intl.formatMessage({ id: "an_error_occurred" }),
        });
        throw err;
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSurveys();
  }, [includeClosed]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadWorksiteSurveys(params) {
    setIsLoadingWorksitesSurveys(true);
    try {
      const result = await countryApiRequest.get("/service_surveys/from_user", {
        params: {
          include_closed: includeClosed,
          for_worksite: true,
          ...Object.fromEntries(params.entries()),
        },
      });
      setSurveys(result);
      return result;
    } catch (err) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      throw err;
    } finally {
      setIsLoadingWorksitesSurveys(false);
    }
  }

  return (
    <div className="min-h-full pt-4 pb-12 flex flex-col bg-white">
      <main className="flex-grow flex flex-col justify-center w-full mx-auto">
        <PageHeading title={intl.formatMessage({ id: "tintometers" })} />
        <h2 className="text-lg mb-2">
          {intl.formatMessage({ id: "KCC_tintometer_configurations" })}
        </h2>
        <div className="flex gap-5 mb-2 flex-col sm:flex-row sm:items-end">
          <div className="flex items-end gap-5 flex-grow">
            <Select
              label={intl.formatMessage({ id: "select_configuration" })}
              value={selectedServiceId}
              options={serviceOptions}
              onChange={(evt) => {
                setSelectedServiceId(parseInt(evt.target.value));
              }}
              className="flex-grow"
            />
            <Toggle
              label={intl.formatMessage({ id: "include_closed_fp" })}
              checked={includeClosed}
              flexAlignCls="flex-row-reverse gap-2 mb-3 sm:mb-2.5"
              onChange={(v) => {
                setIncludeClosed(v);
              }}
            />
          </div>
          <Button
            onClick={async () => {
              await createNewSurvey();
            }}
            paddingCls="w-full px-8 py-2"
            className="mb-3"
            submitting={submittingNewSurvey}
            disabled={!selectedService}
          >
            <PlusCircleIcon className="w-4" />
            <span>{intl.formatMessage({ id: "add" })}</span>
          </Button>
        </div>
        {selectedService &&
          (!tintometerSurveys || tintometerSurveys.length > 0 ? (
            <KCCTintometersSurveysTable
              intl={intl}
              countryCode={country.code}
              surveys={tintometerSurveys}
              colors={colors}
              deleteSurveyCallbackFn={deleteSurvey}
            />
          ) : (
            <h2 className="flex items-center justify-center gap-2 text-gray-400 py-12">
              <ExclamationCircleIcon className="w-6" />
              <span className="font-lg">
                {intl.formatMessage({
                  id: "no_KCC_configurations_for_this_typology",
                })}
              </span>
            </h2>
          ))}
        <h2 className="text-lg mb-4 mt-8">
          {intl.formatMessage({ id: "KCC_worksite_configurations" })}
        </h2>
        {
          <KCCWorksitesSurveysTable
            intl={intl}
            countryCode={country.code}
            // surveys={worksiteSurveys}
            colors={colors}
            datacaller={async (query, columns) => {
              const filters = buildFilterFromQuery(query, columns);
              const ordering = orderingFromQuery(query);
              const params = buildQueryParams(
                query.page,
                query.pageSize,
                filters,
                ordering
              );
              return loadWorksiteSurveys(params);
            }}
            isLoading={isLoadingWorksitesSurveys}
            generateTintometristSurveyCallbackFn={generateTintometristSurvey}
            isTintometristServiceSelected={!!selectedServiceId}
          />
        }
      </main>
    </div>
  );
};

export default Tintometers;
